import { useMediaQuery } from "@mantine/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format, isSameMonth, parseISO } from "date-fns";
import { Suspense, useEffect, useMemo, useState } from "react";

import { ActionBlockButton } from "~/components/buttons/ActionBlockButton.tsx";
import Headline from "~/components/Headline/Headline";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import { useAuth } from "~/contexts/AuthContext";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import { lazyDialog, useLazyDialog } from "~/hooks/useLazyDialog.ts";
import { useStaffMembersAbsences } from "~/modules/absence/api/absence/absenceQueries.ts";
import { useAllAbsenceTypes } from "~/modules/absence/api/absenceType/absenceTypeQueries.ts";
import { useHolidaysByYear } from "~/modules/absence/api/holiday/holidayQueries.ts";
import { useAbsenceActions } from "~/modules/absence/hooks/useAbsenceActions.tsx";
import { getAbsenceActionRules } from "~/modules/absence/utils/absenceRules.ts";
import { downloadAbsenceIcs } from "~/modules/absence/utils/downloadAbsenceIcs.ts";
import { MonthUtilisation } from "~/modules/dashboard/components/MonthUtilisation.tsx";
import { OpenDays } from "~/modules/dashboard/components/OpenDays.tsx";
import { ProjectCards } from "~/modules/dashboard/components/ProjectCards.tsx";
import { type CalendarAbsenceActions, type CalendarActions, getCalendarDays, getCalendarInterval } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { CalendarHeader } from "~/modules/dashboard/components/TimeTrackingCalendar/CalendarHeader.tsx";
import { CompactMonthGrid } from "~/modules/dashboard/components/TimeTrackingCalendar/CompactMonthGrid.tsx";
import { DayColumn } from "~/modules/dashboard/components/TimeTrackingCalendar/DayColumn.tsx";
import { DayList } from "~/modules/dashboard/components/TimeTrackingCalendar/DayList.tsx";
import { MonthGrid } from "~/modules/dashboard/components/TimeTrackingCalendar/MonthGrid.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import type { MonthlyClosingCloseRequest } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { dayStateQueries } from "~/modules/timeTracking/api/timeTracking/timeTrackingQueries.ts";
import { CloseMonthlyClosingModal } from "~/modules/timeTracking/components/CloseMonthlyClosingModal.tsx";
import { TimeTrackingActions } from "~/modules/timeTracking/components/TimeTrackingActions.tsx";
import { useTimeTrackingActions } from "~/modules/timeTracking/hooks/useTimeTrackingActions.ts";
import { isDayBooked, sumMinutesBy } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";

// The sidebar holds the time tracking and absence forms. It loads after the dashboard (see `useLazyDialog`).
const CreateEntrySidebar = lazyDialog(() => import("~/modules/dashboard/components/TimeTrackingCalendar/CreateEntrySidebar.tsx").then((module) => ({ default: module.CreateEntrySidebar })));

type Props = {
	user: User;
};

// The picked day counts in its own month only. Else the day list shows today, or the first of the month.
function getSelectedDate(pickedDate: Date | null, firstOfMonth: Date, today: Date) {
	if (pickedDate && isSameMonth(pickedDate, firstOfMonth)) return pickedDate;
	return isSameMonth(today, firstOfMonth) ? today : firstOfMonth;
}

/**
 * "Meine Zeiterfassung": the time tracking of the dashboard user in a month calendar. Its month is the month of the section.
 *
 * The day view exists from `md` only. Below `md`, the compact grid and the day list already are a day view.
 */
export function TimeTrackingSection({ user }: Props) {
	const dashboardCalendar = useDashboardCalendar();
	const { month, pickedDate, view, setView, scrollToCalendar } = dashboardCalendar;
	const [selectedTimeTrackingId, setSelectedTimeTrackingId] = useState<string | null>(null);
	// One layout only: both layouts would share the selection, and a tap in the day list would open the popover of the hidden grid.
	const isDesktop = useMediaQuery("(min-width: 768px)", undefined, { getInitialValueInEffect: false });
	// A selection from one layout must not open the popover of the other after a resize.
	useEffect(() => {
		setSelectedTimeTrackingId(null);
		setView("month");
	}, [isDesktop, setView]);
	const [closeRequest, setCloseRequest] = useState<MonthlyClosingCloseRequest | null>(null);
	const { hasAnyPermission } = useAuth();

	const controller = useTimeTrackingActions({ userId: user.id, month, keepPreviousMonth: true });
	const mountsCreate = useLazyDialog(CreateEntrySidebar, controller.selectedDate !== null);

	// A grid across a year boundary shows days of two years. For a grid inside one year, both queries are the same.
	const interval = getCalendarInterval(month);
	const holidaysOfFirstYear = useHolidaysByYear(interval.start.getFullYear().toString(), { keepPreviousYear: true });
	const holidaysOfLastYear = useHolidaysByYear(interval.end.getFullYear().toString(), { keepPreviousYear: true });
	const { data, options } = useAggregatedDataV2({
		...controller.queries,
		absenceTypes: useAllAbsenceTypes(),
		holidaysOfFirstYear,
		holidaysOfLastYear,
	});
	const isChangingMonth = controller.isChangingMonth || holidaysOfFirstYear.isPlaceholderData || holidaysOfLastYear.isPlaceholderData;
	// While the new month loads, the queries still hold the old month. The body keeps the month and the day that
	// belong to that data. The header uses `month` and `pickedDate` at once.
	const [shown, setShown] = useState({ month, pickedDate });
	if (!isChangingMonth && (shown.month !== month || shown.pickedDate !== pickedDate)) {
		setShown({ month, pickedDate });
	}
	const shownMonth = isChangingMonth ? shown.month : month;
	const shownPickedDate = isChangingMonth ? shown.pickedDate : pickedDate;
	// A failed read leaves the calendar without markers. The month is not blocked by it.
	const { data: loadedDayStates } = useQuery({ ...dayStateQueries.byMonth(user.id, shownMonth), placeholderData: keepPreviousData });
	// While the new month loads, the placeholder holds the old month, so its markers and count wait for the right month.
	const dayStates = loadedDayStates?.month === shownMonth ? loadedDayStates : undefined;
	const { data: absences, isLoading: absencesAreLoading } = useStaffMembersAbsences(user.staffMemberId ?? "");
	const absenceActionsController = useAbsenceActions({
		absences: absences ?? [],
		absenceTypes: data.absenceTypes ?? [],
		staffMemberId: user.staffMemberId ?? "",
	});
	// A user with no staff member has no absences, so the calendar offers no absence action and no absence tab.
	const hasStaffMember = !!user.staffMemberId;
	const userCanManageAbsences = hasAnyPermission(PermissionNames.CanManageAbsences);
	const absenceActions: CalendarAbsenceActions | undefined = hasStaffMember ? {
		getRules: (absence, absenceTypeName) => getAbsenceActionRules(absence, absenceTypeName, userCanManageAbsences),
		openEdit: absenceActionsController.openEdit,
		openDelete: absenceActionsController.openDelete,
		download: (absence, absenceTypeName) => downloadAbsenceIcs({
			absence,
			absenceTypeName,
			userEmail: user.email,
			userFullName: user.fullName,
		}),
	} : undefined;

	const days = useMemo(() => {
		const holidays = data.holidaysOfFirstYear === data.holidaysOfLastYear
			? data.holidaysOfFirstYear ?? []
			: [...data.holidaysOfFirstYear ?? [], ...data.holidaysOfLastYear ?? []];
		return getCalendarDays({
			absences,
			absenceTypes: data.absenceTypes,
			holidays,
			month: shownMonth,
			timeTrackings: controller.timeTrackings,
		});
	}, [absences, controller.timeTrackings, data.absenceTypes, data.holidaysOfFirstYear, data.holidaysOfLastYear, shownMonth]);

	const today = new Date();
	const headerDate = getSelectedDate(pickedDate, parseISO(`${month}-01`), today);
	const selectedDate = getSelectedDate(shownPickedDate, parseISO(`${shownMonth}-01`), today);
	const selectedDay = days.find((day) => day.isInMonth && day.date.getDate() === selectedDate.getDate());
	const isBooked = selectedDay ? isDayBooked(dayStates, selectedDay.date) : false;

	const selectDate = (date: Date) => {
		dashboardCalendar.selectDate(date);
		setSelectedTimeTrackingId(null);
		// Below `md`, the day details sit below the grid, so a tap shows them from the top of the section.
		if (!isDesktop) scrollToCalendar();
	};

	const goToMonth = (newMonth: string) => {
		dashboardCalendar.goToMonth(newMonth);
		setSelectedTimeTrackingId(null);
	};

	const goToDate = (date: Date) => {
		dashboardCalendar.goToDate(date);
		setSelectedTimeTrackingId(null);
	};

	const actions: CalendarActions = {
		absenceActions,
		canAddTrackingOn: controller.canAddTrackingOn,
		canAddAbsence: hasStaffMember,
		showDisabledAddTracking: controller.hasClosingExceptionsOrUnlocks,
		openCreate: controller.openCreate,
		openUpdate: controller.openUpdate,
		openDelete: controller.openDelete,
		selectedTimeTrackingId,
		setSelectedTimeTrackingId,
	};

	/**
	 * A load dims the calendar and blocks its clicks, but keeps it on screen. The first load shows the days without
	 * entries, and a month change keeps the old month. So the page keeps its height and a user who scrolled down sees no jump.
	 */
	const isLoading = options.loadingPercent < 100 || controller.timeTrackingsAreLoading || controller.closingIsLoading || isChangingMonth;
	// Until the absences are in, a day without entries is not known to be empty.
	const hideEmptyHint = isLoading || absencesAreLoading;

	const calendar = isDesktop && view === "day" ? (
		<div className="flex gap-3 p-3">
			<div className="shrink-0 rounded-lg border border-gray-200 bg-white">
				<DayColumn key={shownMonth}
						   days={days}
						   selectedDate={selectedDate}
						   selectDate={selectDate} />
			</div>
			{selectedDay && (
				// The card is absolute, so its content does not set the height. The row gives it the height of the day column.
				<div className="relative min-w-0 flex-1">
					<div className="absolute inset-0 flex">
						<DayList day={selectedDay}
								 card
								 isBooked={isBooked}
								 emptyHint={hideEmptyHint ? undefined : "block"}
								 {...actions} />
					</div>
				</div>
			)}
		</div>
	) : isDesktop ? (
		<MonthGrid days={days}
				   dayStates={dayStates}
				   openDay={(date) => {
					   selectDate(date);
					   setView("day");
				   }}
				   {...actions} />
	) : (
		<div className="flex flex-col">
			<div className="p-3">
				<CompactMonthGrid days={days}
								  dayStates={dayStates}
								  selectedDate={selectedDate}
								  selectDate={selectDate} />
			</div>
			{selectedDay && (
				<div className="border-t border-gray-200 p-3">
					<DayList day={selectedDay}
							 primaryAdd
							 isBooked={isBooked}
							 emptyHint={hideEmptyHint ? undefined : "text"}
							 {...actions} />
				</div>
			)}
		</div>
	);

	const closeBlock = controller.monthlyClosing?.close;
	// The API has no month figure for a staffing, so the card sums the trackings that the calendar loads for the
	// shown month. `minutesTracked` of the staffing is all time and does not fit.
	const trackedMinutesByStaffing = useMemo(
		() => sumMinutesBy(controller.timeTrackings ?? [], (timeTracking) => timeTracking.staffingId),
		[controller.timeTrackings],
	);

	const trackedMinutesByType = useMemo(
		() => sumMinutesBy(controller.timeTrackings ?? [], (timeTracking) => timeTracking.timeTrackingTypeId),
		[controller.timeTrackings],
	);

	return (
		<>
			<div className="mb-3 md:flex md:items-start md:gap-4">
				<div className="flex min-h-7 items-center justify-between gap-4 md:justify-start">
					<Headline type="h3" size="lg" weight="semibold">Zeiterfassung</Headline>
					{(closeBlock || controller.closingIsLoading) && (
						// The button follows the month of the calendar, not the month of the closing read that is still on screen.
						// The first load shows it disabled, so it does not pop in. A failed read hides it.
						<ActionBlockButton block={!closeBlock || isChangingMonth ? { allowed: false, reason: null } : closeBlock}
										   size="sm"
										   theme="primary"
										   onClick={() => setCloseRequest({ scope: { scope: "all" }, label: "den ganzen Monat" })}>
							Monatsabschluss
						</ActionBlockButton>
					)}
				</div>
				{/* Below `md`, the utilisation and the open days wrap under the heading row, so the heading row keeps one line. */}
				<div className="mt-1 md:ml-auto md:mt-0">
					<MonthUtilisation />
					<OpenDays />
				</div>
			</div>
			<div className="mb-3">
				<ProjectCards trackedMinutesByStaffing={trackedMinutesByStaffing}
							  trackedMinutesByType={trackedMinutesByType}
							  isFirstLoad={controller.timeTrackingsAreLoading || controller.closingIsLoading}
							  isChangingMonth={controller.isChangingMonth} />
			</div>
			<div className="flex flex-col gap-3">
				{/* `scrollToCalendar()` scrolls this box to the top of the window, not the section: the heading and the
				    project cards are above it. Below `lg`, the scroll margin is the height of the sticky mobile bar (4rem)
				    plus the top inset. The bar gets taller by the inset in the home screen app, and without the margin it
				    hides the calendar header. */}
				<div ref={dashboardCalendar.calendarRef}
					 className="relative scroll-mt-[calc(4rem+env(safe-area-inset-top))] overflow-hidden rounded-lg border border-gray-200 max-md:bg-white lg:scroll-mt-0"
					 aria-busy={isLoading}>
					<CalendarHeader month={month}
									onMonthChange={goToMonth}
									onToday={() => goToMonth(format(today, "yyyy-MM"))}
									selectedDate={headerDate}
									onDayChange={goToDate}
									view={view}
									onViewChange={isDesktop ? (newView) => {
										setView(newView);
										setSelectedTimeTrackingId(null);
									} : undefined} />
					{calendar}
					{isLoading && (
						<div className="absolute inset-0 flex items-center justify-center bg-white/60">
							<LoadingSpinner theme="neutral"
										 size="lg" />
						</div>
					)}
				</div>
				{mountsCreate && (
					<Suspense fallback={null}>
						<CreateEntrySidebar controller={controller}
											absencePanelInputs={hasStaffMember ? absenceActionsController.createPanelInputs : null} />
					</Suspense>
				)}
				<TimeTrackingActions controller={controller}
									 hideCreate />
				{absenceActionsController.dialogs}
				<CloseMonthlyClosingModal month={month}
										  userId={user.id}
										  request={closeRequest}
										  onClose={() => setCloseRequest(null)} />
			</div>
		</>
	);
}
